import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAuthSession, getAuthSession, ALL_TENANTS_SENTINEL, getActiveTenantId, setActiveTenant } from '../services/auth';
import apiClient from '../api/client';
import { useTheme } from '../hooks/useTheme';
import {
  Plus,
  Wrench,
  LayoutDashboard,
  Shield,
  Store,
  MessageSquare,
  Play,
  Rocket,
  User,
  Zap,
  Database,
  GitBranch,
  Trash2,
  Settings2,
  Archive,
  BarChart3,
  Receipt,
  Sun,
  Moon,
  Gem,
  ChevronDown,
  LogOut,
  UserCog,
  Building2,
  Users,
  Radio,
  Webhook,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  isSub?: boolean;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // 三大中心 + 平台管理
  const trainingItems: NavItem[] = [
    { path: '/', label: 'Agent 工作台', icon: LayoutDashboard },
    { path: '/teaching', label: '示教中心', icon: MessageSquare },
    { path: '/material-lane', label: '素材工坊', icon: Rocket },
    { path: '/testing', label: '测试验证', icon: Play },
  ];

  const assetItems: NavItem[] = [
    { path: '/marketplace', label: '能力市场', icon: Store },
    { path: '/marketplace?tab=persona', label: '人设库', icon: User, isSub: true },
    { path: '/marketplace?tab=skill', label: '技能库', icon: Zap, isSub: true },
    { path: '/marketplace?tab=knowledge', label: '知识库', icon: Database, isSub: true },
    { path: '/marketplace?tab=mcp', label: '工具 / MCP 库', icon: Wrench, isSub: true },
    { path: '/marketplace?tab=workflow', label: '工作流库', icon: GitBranch, isSub: true },
  ];

  const runtimeItems: NavItem[] = [
    { path: '/instances', label: '运行时大盘', icon: BarChart3 },
    { path: '/billing', label: '账单', icon: Receipt },
    { path: '/archives', label: '归档中心', icon: Archive },
  ];

  const platformItems: NavItem[] = [
    { path: '/admin/tenants', label: '租户管理', icon: Shield },
    { path: '/admin/users', label: '用户管理', icon: Users },
    { path: '/admin/channels', label: '模型通道', icon: Radio },
    { path: '/admin/webhooks', label: 'Webhook', icon: Webhook },
    { path: '/admin/gc', label: '镜像清理', icon: Trash2 },
    { path: '/settings', label: '系统设置', icon: Settings2 },
  ];

  const isActive = (path: string) => {
    const [basePath, query] = path.split('?');
    if (basePath === '/') return location.pathname === '/';
    // 子路由保持高亮（如 /teaching/xxx → 示教中心）
    if (location.pathname !== basePath && !location.pathname.startsWith(basePath + '/')) return false;
    const currentTab = new URLSearchParams(location.search).get('tab');
    if (!query) {
      // 无 tab 的父项（能力市场）：URL 带 tab 时让给对应子项高亮
      return currentTab === null;
    }
    const itemTab = new URLSearchParams(query).get('tab');
    return currentTab === itemTab;
  };

  return (
    <div className="app-layout">
      {/* 侧边栏 */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo.svg" alt="Atelier" width="24" height="24" />
          <span>Atelier</span>
        </div>

        <div style={{ padding: '0 12px', marginBottom: '12px' }}>
          <button
            className="primary"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => navigate('/agents/create')}
          >
            <Plus size={16} />
            新建 Agent
          </button>
        </div>

        <nav className="sidebar-nav">
          <SectionTitle title="智能体培训中心" />
          {trainingItems.map((item) => renderNavItem(item, isActive, navigate))}

          <SectionTitle title="智能体资产中心" />
          {assetItems.map((item) => renderNavItem(item, isActive, navigate))}

          <SectionTitle title="智能体运行中心" />
          {runtimeItems.map((item) => renderNavItem(item, isActive, navigate))}

          <SectionTitle title="平台管理" />
          {platformItems.map((item) => renderNavItem(item, isActive, navigate))}
        </nav>

        <div className="sidebar-footer">Atelier 琢玉坊 v1.0</div>
      </aside>

      {/* 主内容区 */}
      <main className="main-content">
        <div className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Agent 运行时管理中心
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a
              className="icon-btn"
              href={`${window.location.protocol}//${window.location.hostname}:25102`}
              target="_blank"
              rel="noreferrer"
              title="打开练玉坊（Agent 练习工作台）"
              aria-label="打开练玉坊"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', padding: '0 10px' }}
            >
              <Gem size={16} />
              <span style={{ fontSize: '13px' }}>练玉坊</span>
            </a>
            <TenantSwitcher />
            <ThemeToggle />
            <UserInfo />
          </div>
        </div>
        <div className="main-content-inner">{children}</div>
      </main>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div style={{ marginTop: '14px', padding: '0 8px' }}>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '6px',
        }}
      >
        {title}
      </div>
    </div>
  );
}

function renderNavItem(item: NavItem, isActive: (p: string) => boolean, navigate: (p: string) => void) {
  const Icon = item.icon;
  return (
    <button
      key={item.path}
      className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
      style={item.isSub ? { paddingLeft: 36, fontSize: '0.92em' } : undefined}
      onClick={() => navigate(item.path)}
    >
      <Icon size={item.isSub ? 16 : 18} />
      <span>{item.label}</span>
    </button>
  );
}

interface TenantOption {
  id: string;
  name: string;
  slug?: string;
}

/**
 * 顶栏租户切换器：仅平台超管可见（/auth/me 的 platform_role 大小写不敏感比较）。
 * 选项 = 「全部空间」（哨兵 'all'）+ 全部租户；切换写 localStorage 并刷新页面使全站生效。
 */
function TenantSwitcher() {
  const [role, setRole] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [current, setCurrent] = useState(getActiveTenantId());

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ platform_role?: string }>('/auth/me')
      .then((meRes) => {
        if (cancelled) return null;
        const meRole = meRes.data?.platform_role || '';
        setRole(meRole);
        if (meRole.toLowerCase() === 'super_admin') {
          return apiClient.get<TenantOption[]>('/tenants');
        }
        return null;
      })
      .then((tenantsRes) => {
        if (cancelled || !tenantsRes) return;
        setTenants(tenantsRes.data ?? []);
      })
      .catch(() => {
        // 角色确认失败按普通用户降级，不阻塞页面渲染
        if (!cancelled) setRole('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 角色确认完成前不渲染，避免「非超管瞬间闪现选择器」
  if (role === null) return null;
  if (role.toLowerCase() !== 'super_admin') return null;

  const switchTenant = (value: string) => {
    const isAll = value === ALL_TENANTS_SENTINEL;
    const tenant = tenants.find((t) => t.id === value);
    setActiveTenant(isAll ? ALL_TENANTS_SENTINEL : value, isAll ? ALL_TENANTS_SENTINEL : tenant?.slug || value);
    setCurrent(value);
    // 刷新页面使全站（client.ts 租户头注入、各路由页）立即生效
    window.location.reload();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Building2 size={14} style={{ color: 'var(--text-muted)' }} />
      <select
        className="input"
        aria-label="租户空间"
        title="切换租户空间"
        value={current}
        onChange={(e) => switchTenant(e.target.value)}
        style={{ width: 'auto', maxWidth: 200, padding: '4px 8px', fontSize: 13 }}
      >
        <option value={ALL_TENANTS_SENTINEL}>全部空间</option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      className="icon-btn"
      onClick={toggleTheme}
      title={isDark ? '切换到浅色模式' : '切换到深色模式'}
      aria-label={isDark ? '切换到浅色模式' : '切换到深色模式'}
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

function UserInfo() {
  const navigate = useNavigate();
  // 会话信息仅在挂载时读取一次（与原 useEffect([]) 语义一致）
  const [session] = useState(getAuthSession);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userName = session?.userName || '用户';

  // 点击菜单外部时收起
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const logout = () => {
    clearAuthSession();
    window.location.href = '/login';
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="账号菜单"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: menuOpen ? 'var(--bg-hover, rgba(128,128,128,0.12))' : 'transparent',
          transition: 'background 0.2s',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
          }}
        >
          {userName.charAt(0).toUpperCase()}
        </div>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userName}</span>
        <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
      </button>
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 160,
            background: 'var(--bg-secondary, var(--glass-bg))',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.18))',
            padding: 6,
            zIndex: 200,
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={() => {
              setMenuOpen(false);
              navigate('/profile');
            }}
          >
            <UserCog size={14} />
            个人设置
          </button>
          <button type="button" role="menuitem" className="user-menu-item" onClick={logout}>
            <LogOut size={14} />
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}
